/**
 * HeartMark — the PSPulse brand signature.
 *
 * A crafted SVG heart path — NOT the default ♥ Unicode glyph. The path has
 * a deliberately ASYMMETRIC left/right lobe and an ELONGATED, gently-curved
 * tip that settles lower than a symmetric heart would, giving it a hand-drawn,
 * warm quality (Oura warmth, not a clinical icon, not the flat emoji).
 *
 * Asymmetry notes:
 *   - Left lobe is slightly wider and more open (control point at x≈2.2).
 *   - Right lobe is a touch narrower and higher (control point at x≈21.8).
 *   - The tip curves to x≈11.6 (off-centre) and dips to y≈22.4 (elongated),
 *     then curves back with a subtle leftward lean — like a real drawn heart.
 *
 * Two scales, one component:
 *   - Hero scale (size ≥ 12): full path, beats via the CSS `beat` keyframe.
 *   - Dot scale (size ≤ 11): same path at small size, reads as a warm rounded
 *     dot — keeping the brand mark consistent rather than mixing a glyph and a
 *     plain circle.
 *
 * The `beat` animation period is controlled by `--beat-dur` (default 1.7s),
 * which the parent sets inline to encode per-element busyness. Both the `beat`
 * and the companion `ring` animations read the same variable so they stay
 * synchronised.
 *
 * Accessibility: decorative instances carry `aria-hidden="true"`. The hero
 * number's accessible name comes from the surrounding text/aria-label, not
 * from this mark.
 */

interface HeartMarkProps {
  /** px size (sets width + height). Defaults to "1em" (inherits from font-size). */
  size?: number | string;
  /**
   * Extra CSS class names. Add `heart-mark` to enable the CSS `beat` animation;
   * omit it for a static mark (e.g. no-data cards where nothing is live).
   */
  className?: string;
}

/**
 * Crafted heart path (24×24 viewBox).
 *
 * Key asymmetries vs. a symmetric heart:
 *   - Left lobe opens wider and sits slightly lower (anchor at 6.8, 3.6)
 *   - Right lobe is narrower/higher (anchor at 17.4, 3.5)
 *   - Tip elongates to y=22.4 and lands at x=11.6 (slightly right-of-centre),
 *     with a long, gentle leftward curve on the return — giving the tip a
 *     natural, drawn-in quality rather than a sharp symmetric V.
 */
const HEART_PATH =
  "M11.6 22.4" +
  " C 7.0 18.5, 2.2 14.6, 1.8 9.8" +
  " C 1.4 6.3, 3.7 3.6, 6.8 3.6" +
  " C 9.0 3.6, 10.8 4.9, 11.6 6.8" +
  " C 12.5 5.0, 14.5 3.5, 17.4 3.5" +
  " C 20.5 3.5, 22.6 6.4, 22.1 9.9" +
  " C 21.4 14.8, 16.6 18.6, 11.6 22.4 Z";

export function HeartMark({ size = "1em", className = "heart-mark" }: HeartMarkProps) {
  const dim = typeof size === "number" ? `${size}px` : size;

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={dim}
      height={dim}
      role="img"
      aria-hidden="true"
      focusable="false"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    >
      <path fill="currentColor" d={HEART_PATH} />
    </svg>
  );
}
